import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileDown, AlertTriangle, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { parseRiskImport, downloadRiskImportTemplate, downloadBikeSampleRisks } from "../lib/riskImport";
import { useEstimate } from "../store/EstimateContext";

export default function RiskImporter() {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { applyImportedRiskPlan } = useEstimate();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const parsed = await parseRiskImport(file);
      applyImportedRiskPlan(parsed, mode);
      setResult({
        ok: true, mode, filename: file.name,
        risks: parsed.riskCount, assumptions: parsed.assumptionsCount, dependencies: parsed.dependenciesCount,
        warnings: parsed.warnings,
      });
      toast.success(t("importer.successToast"), {
        description: `${parsed.riskCount} risks · ${parsed.assumptionsCount} assumptions · ${parsed.dependenciesCount} dependencies (${mode})`,
      });
    } catch (err) {
      setResult({ ok: false, filename: file.name, error: String(err.message || err) });
      toast.error(t("importer.failureToast"), { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-300 p-4" data-testid="risk-importer">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("importer.label")}
            </p>
          </div>
          <h3 className="font-heading text-base tracking-tight font-bold text-black">
            {t("importer.title")}
          </h3>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            CSV / XLSX with <span className="font-mono">RISKS</span>, <span className="font-mono">ASSUMPTIONS</span>,{" "}
            <span className="font-mono">DEPENDENCIES</span> blocks. Round-trips the Bid Pack risk register.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-gray-300" data-testid="risk-import-mode-toggle">
            <button type="button" onClick={() => setMode("replace")}
              data-testid="risk-import-mode-replace"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${mode === "replace" ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}>
              {t("importer.modeReplace")}
            </button>
            <button type="button" onClick={() => setMode("merge")}
              data-testid="risk-import-mode-merge"
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-l border-gray-300 ${mode === "merge" ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}>
              {t("importer.modeMerge")}
            </button>
          </div>
          <button type="button" onClick={downloadRiskImportTemplate}
            data-testid="risk-import-template-btn"
            className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <FileDown size={12} /> {t("importer.templateBtn")}
          </button>
          <button type="button" onClick={downloadBikeSampleRisks}
            data-testid="risk-import-bike-sample-btn"
            title="Download a populated S1000D Bike sample risk register"
            className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <FileDown size={12} /> Bike sample
          </button>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
            data-testid="risk-import-btn"
            className="bg-[#002FA7] text-white hover:bg-black rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed">
            {busy ? <><Loader2 size={12} className="animate-spin" /> {t("importer.importingBtn")}</> : <><Upload size={12} /> {t("importer.importBtn")}</>}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
            onChange={handleFile} data-testid="risk-import-file-input" className="hidden" />
        </div>
      </div>
      {result && (
        <div className="mt-3 border-t border-gray-200 pt-3 space-y-1.5" data-testid="risk-import-result">
          {result.ok ? (
            <div className="flex items-center gap-2 text-xs">
              <CheckCircle2 size={14} className="text-[#15803D]" />
              <span className="font-bold">{result.filename}</span>
              <span className="text-gray-500 font-mono">
                · {result.risks} risks · {result.assumptions} assum. · {result.dependencies} deps · {result.mode}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-[#FF2400]">
              <X size={14} /> <span className="font-bold">{result.filename}</span>
              <span className="font-mono">{result.error}</span>
            </div>
          )}
          {result.warnings?.length > 0 && (
            <ul className="text-[11px] text-[#CA8A04] font-mono space-y-0.5" data-testid="risk-import-warnings">
              {result.warnings.map((w, i) => (
                <li key={i} className="flex items-center gap-1"><AlertTriangle size={11} /> {w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
